/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { DataModel } from "../../model/types";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import fitty from "fitty";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Utils } from "../../utils/utils";
import domtoimage from "dom-to-image";
import { isEqual } from "lodash";
dayjs.extend(relativeTime);
export class DiscoveryDisplayComponent {
    constructor() {
        this.defOptions = Object.assign(Object.assign({}, new Param()), { responsive: true });
        this.divider = 1000;
        this.initial = false;
        this.chartOptions = Object.assign(Object.assign({}, new Param()), { hideXAxis: true, hideYAxis: true, bgColor: 'transparent', scheme: 'ATLANTIS' });
        this.result = undefined;
        this.type = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.unit = '';
        this.parsing = false;
        this.rendering = false;
        this.message = undefined;
        this.innerStyle = undefined;
        this.innerOptions = undefined;
    }
    updateRes() {
        this.result = GTSLib.getData(this.result);
        this.message = this.convert(this.result || new DataModel());
        this.flexFont();
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!isEqual(opts, this.innerOptions)) {
            this.innerOptions = Object.assign({}, opts);
            this.chartOptions = Object.assign(Object.assign({}, this.chartOptions), { fontColor: this.innerOptions.fontColor });
            this.message = this.convert(this.result || new DataModel());
            this.flexFont();
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue }, this.chartOptions);
        }
    }
    discoveryEventHandler(event) {
        var _a;
        const res = Utils.parseEventData(event.detail, ((_a = this.innerOptions) === null || _a === void 0 ? void 0 : _a.eventHandler) || '', this.el.id);
        if (res.style) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), res.style);
        }
    }
    async resize() {
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w;
        this.height = dims.h;
        this.flexFont();
        return Promise.resolve();
    }
    // noinspection JSUnusedLocalSymbols
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async export(type = 'png') {
        let bgColor = Utils.getCSSColor(this.el, '--warp-view-bg-color', 'transparent');
        bgColor = ((this.innerOptions) || { bgColor }).bgColor || bgColor;
        const dm = ((this.result || {
            globalParams: { bgColor }
        }).globalParams || { bgColor });
        bgColor = dm.bgColor || bgColor;
        return await domtoimage.toPng(this.pngWrapper, { height: this.height, width: this.width, bgcolor: bgColor });
    }
    componentWillLoad() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoveryDisplayComponent, this.debug);
        if (typeof this.options === 'string') {
            this.innerOptions = JSON.parse(this.options);
        }
        else {
            this.innerOptions = this.options;
        }
        this.chartOptions = Object.assign(Object.assign({}, this.chartOptions), { fontColor: this.innerOptions.fontColor });
        this.result = GTSLib.getData(this.result);
        this.divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
        this.message = this.convert(this.result || new DataModel());
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.innerOptions,
        });
    }
    componentDidLoad() {
        setTimeout(() => {
            this.height = Utils.getContentBounds(this.el.parentElement).h;
            this.initial = true;
            this.flexFont();
            this.parsing = false;
        });
    }
    // noinspection JSUnusedGlobalSymbols
    disconnectedCallback() {
        if (this.timer) {
            clearInterval(this.timer);
        }
        if (this.fitties) {
            this.fitties.unsubscribe();
        }
    }
    convert(dataModel) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!!this.timer) {
            clearInterval(this.timer);
        }
        let options = Utils.mergeDeep(this.defOptions, this.innerOptions || {});
        options = Utils.mergeDeep(options || {}, dataModel.globalParams);
        this.innerOptions = Object.assign({}, options);
        if (this.innerOptions.customStyles) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), this.innerOptions.customStyles || {});
        }
        this.chartOptions = Object.assign(Object.assign({}, this.chartOptions), { fontColor: this.innerOptions.fontColor });
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['convert'], 'dataModel', dataModel);
        let display;
        if (!!dataModel.data) {
            display = GTSLib.isArray(dataModel.data) ? dataModel.data[0] : dataModel.data;
        }
        else {
            display = GTSLib.isArray(dataModel) ? dataModel[0] : dataModel;
        }
        display = GTSLib.isArray(display) ? display[0] : display;
        if (GTSLib.isGts(display)) {
            this.gts = [display];
            let v = (_c = (_b = this.innerOptions.display) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '';
            if (((_d = this.innerOptions.display) === null || _d === void 0 ? void 0 : _d.value) === undefined && display.v && display.v.length > 0) {
                const dataPoint = display.v[display.v.length - 1];
                v = dataPoint[dataPoint.length - 1];
                if ((_e = this.innerOptions.display) === null || _e === void 0 ? void 0 : _e.decimals) {
                    const dec = Math.pow(10, (_g = (_f = this.innerOptions.display) === null || _f === void 0 ? void 0 : _f.decimals) !== null && _g !== void 0 ? _g : 2);
                    v = Math.round(parseFloat(v + '') * dec) / dec;
                }
            }
            display = v;
        }
        if (display && display.hasOwnProperty('text')) {
            if (display.hasOwnProperty('url')) {
                display = `<a href="${display.url}" target="_blank">${display.text}</a>`;
            }
            else {
                display = display.text;
            }
        }
        switch (this.innerOptions.timeMode) {
            case 'date':
                if (display && !isNaN(parseInt(display, 10))) {
                    display = GTSLib.toISOString(parseInt(display, 10), this.divider, this.innerOptions.timeZone, this.innerOptions.fullDateDisplay ? this.innerOptions.timeFormat : undefined);
                }
                break;
            case 'duration':
                if (display && !isNaN(parseInt(display, 10))) {
                    const start = GTSLib.toISOString(parseInt(display, 10), this.divider, this.innerOptions.timeZone, this.innerOptions.fullDateDisplay ? this.innerOptions.timeFormat : undefined);
                    display = this.displayDuration(dayjs(start));
                }
                break;
            case 'custom':
            case 'timestamp':
                display = decodeURIComponent(decodeURIComponent(display));
        }
        return display;
    }
    flexFont() {
        var _a;
        if (!!this.wrapper) {
            const height = Utils.getContentBounds(this.wrapper.parentElement).h - 20;
            if (height !== this.innerHeight) {
                this.innerHeight = height;
                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['flexFont'], height);
                if (this.fitties) {
                    this.fitties.unsubscribe();
                }
                if (this.innerOptions.responsive) {
                    this.fitties = fitty(this.wrapper, { maxSize: height * 0.80, minSize: 14 });
                    this.fitties.element.addEventListener('fit', () => {
                        if (this.initial) {
                            setTimeout(() => this.draw.emit());
                            this.initial = false;
                        }
                    });
                    this.fitties.fit();
                }
                else {
                    if (this.initial) {
                        setTimeout(() => this.draw.emit());
                        this.initial = false;
                    }
                }
            }
        }
    }
    render() {
        var _a, _b, _c, _d, _e, _f, _g;
        return h("div", { key: 'e7ba68f5ddaa5476a62c23198276b70d2afccd9f', ref: (el) => this.pngWrapper = el, class: "png-wrapper" }, h("style", { key: '5ff27cd5f95d415aa91b7c2bb6848abdd10c4ae6' }, this.generateStyle(this.innerStyle)), h("div", { key: '972f85d462532a4f2b281dd16f81ee9b397d91a5', style: { color: this.innerOptions.fontColor }, class: (_c = 'display-container pos-' + ((_b = (_a = this.innerOptions) === null || _a === void 0 ? void 0 : _a.display) === null || _b === void 0 ? void 0 : _b.labelPosition)) !== null && _c !== void 0 ? _c : 'c' }, this.parsing ? h("discovery-spinner", null, "Parsing data...") : '', this.rendering ? h("discovery-spinner", null, "Rendering data...") : '', h("div", { key: '8121afce285439319de78dfe20867074399aa008', ref: (el) => this.wrapper = el, class: "value" }, h("span", { key: 'e4ff15a6644857310187605e4151accff36fc9dc', innerHTML: this.message }), h("small", { key: '37fd520df682db58a51de389085fbc6c67dec276' }, this.innerOptions.unit || this.unit || ''))), this.gts && ((_d = this.innerOptions.display) === null || _d === void 0 ? void 0 : _d.showChart)
            ? h("div", { class: "chart-wrapper" }, h("div", null, h("discovery-tile-result", { url: "", result: (_e = this.gts) !== null && _e !== void 0 ? _e : [], type: (_g = (_f = this.innerOptions.display) === null || _f === void 0 ? void 0 : _f.chartType) !== null && _g !== void 0 ? _g : 'area', options: this.chartOptions })))
            : '');
    }
    displayDuration(start) {
        this.timer = setInterval(() => this.message = dayjs().to(start), 1000);
        return dayjs().to(start);
    }
    generateStyle(innerStyle) {
        return Object.keys(innerStyle || {}).map(k => k + ' { ' + innerStyle[k] + ' }').join('\n');
    }
    static get is() { return "discovery-display"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-display.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-display.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "unit",
                "reflect": false,
                "defaultValue": "''"
            }
        };
    }
    static get states() {
        return {
            "width": {},
            "height": {},
            "parsing": {},
            "rendering": {},
            "message": {},
            "innerStyle": {},
            "innerOptions": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        },
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }];
    }
    static get listeners() {
        return [{
                "name": "discoveryEvent",
                "method": "discoveryEventHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=discovery-display.js.map
